import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Package, CheckCircle, ArrowLeft, ArrowRight, Phone,
  Receipt, Wallet, Clock, WhatsappLogo, Sparkle, Ruler, Star, CurrencyInr
} from "@phosphor-icons/react";
import PayOnlineDialog from "@/components/PayOnlineDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact 5-step visual (BOOKED · DESIGN · CUT · READY · DELIVER)
const VISUAL_STEPS = [
  { key: "new_order",      label: "BOOKED"  },
  { key: "design_pending", label: "DESIGN"  },
  { key: "in_cutting",     label: "CUT"     },
  { key: "completed",      label: "READY"   },
  { key: "delivered",      label: "DELIVER" },
];

function currentVisualStep(status) {
  // Map any backend status onto the 5-step visual journey.
  const map = {
    draft: 0,
    new_order: 0,
    design_pending: 1,
    ready_to_cut: 2,
    in_cutting: 2,
    completed: 3,
    delivered: 4,
  };
  return map[status] ?? 0;
}

const STATUS_META = {
  draft:          { label: "DRAFT",       tone: "slate"   },
  new_order:      { label: "NEW ORDER",   tone: "orange"  },
  design_pending: { label: "DESIGN PENDING", tone: "cyan" },
  ready_to_cut:   { label: "READY TO CUT",tone: "amber"   },
  in_cutting:     { label: "IN CUTTING",  tone: "orange"  },
  completed:      { label: "READY",       tone: "emerald" },
  delivered:      { label: "DELIVERED",   tone: "emerald" },
};

export default function MyOrders() {
  const { mobile } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  // "Pay Now" state: `payTarget` = the specific order object being paid.
  // `pickerOpen` = the balance-summary picker (multi-order case).
  // `combinedPay` = { total, orderNumbers[] } — "PAY TOTAL" one-shot mode.
  const [payTarget, setPayTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [combinedPay, setCombinedPay] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await axios.get(`${API}/public/track-by-mobile/${mobile}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) {
          setErr(
            e.response?.status === 404
              ? "No orders found for this mobile number."
              : e.response?.data?.detail || "Unable to load orders. Please try again."
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [mobile]);

  const maskedMobile = mobile ? `${mobile.slice(0, 2)}****${mobile.slice(-4)}` : "";
  const nameUpper = (data?.customer_name || "Customer").toUpperCase();
  const activeCount = data?.summary?.active_orders ?? 0;
  const totalCount = data?.summary?.total_orders ?? 0;

  return (
    <div className="min-h-screen bp-grid relative overflow-hidden" data-testid="my-orders-page">
      {/* Corner sparkle decorations */}
      <Sparkle className="absolute top-8 right-10 text-slate-800/60 pointer-events-none" size={56} weight="fill" />
      <Sparkle className="absolute bottom-24 left-8 text-slate-800/40 pointer-events-none" size={40} weight="fill" />

      {/* Header */}
      <header className="border-b border-slate-800/60 bg-slate-950/60 backdrop-blur relative z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => nav("/login")}
            className="text-slate-400 hover:text-white p-1 rounded-sm hover:bg-slate-800"
            data-testid="my-orders-back"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <img
            src="/logo.png"
            alt="Shobhit Creation"
            className="h-9 w-auto object-contain shrink-0"
            data-testid="brand-logo"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono-num text-orange-400 tracking-widest">
              // YOUR ORDERS · {maskedMobile}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6 relative z-10">
        {loading && (
          <div className="surface p-12 rounded-sm text-center text-slate-400 font-mono-num">
            Loading your orders…
          </div>
        )}

        {err && !loading && (
          <div className="surface p-10 rounded-sm text-center" data-testid="my-orders-error">
            <div className="w-14 h-14 stripe-accent mx-auto mb-4" />
            <div className="font-heading text-2xl font-black text-white">{err}</div>
            <div className="text-slate-400 mt-2 text-sm">
              Please check the mobile number or contact the workshop.
            </div>
            <Button
              onClick={() => nav("/login")}
              className="mt-6 rounded-sm h-11 btn-industrial px-5"
              data-testid="my-orders-try-again"
            >
              TRY ANOTHER NUMBER
            </Button>
          </div>
        )}

        {data && !loading && (
          <>
            {/* HERO: Greeting + Summary tiles */}
            <section className="surface p-6 md:p-10 rounded-sm relative overflow-hidden">
              <Sparkle className="absolute top-4 right-6 text-slate-700/40 pointer-events-none" size={40} weight="fill" />
              <h1
                className="font-heading font-black text-white leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
                data-testid="my-orders-greeting"
              >
                NAMASTE, <span className="text-orange-400">{nameUpper}</span> JI
              </h1>
              <p className="text-slate-400 text-sm md:text-base mt-3">
                {totalCount === 0
                  ? "No orders yet."
                  : activeCount > 0
                  ? <>Aapke <b className="text-emerald-400 font-mono-num">{activeCount}</b> active order{activeCount === 1 ? "" : "s"} hai.</>
                  : <>Aapke saare {totalCount} order complete ho chuke hain 🎉</>}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-6">
                <SummaryTile
                  testid="summary-total"
                  label="Total"
                  value={data.summary.total_amount}
                  tone="blue"
                  icon={<Receipt size={18} weight="bold"/>}
                />
                <SummaryTile
                  testid="summary-paid"
                  label="Paid"
                  value={data.summary.total_paid}
                  tone="emerald"
                  icon={<CheckCircle size={18} weight="bold"/>}
                />
                <SummaryTile
                  testid="summary-balance"
                  label="Balance"
                  value={data.summary.total_balance}
                  tone={data.summary.total_balance > 0 ? "orange" : "emerald"}
                  icon={<Wallet size={18} weight="bold"/>}
                  highlight={data.summary.total_balance > 0}
                  onClick={data.summary.total_balance > 0 ? () => setPickerOpen(true) : undefined}
                  hint={data.summary.total_balance > 0 ? "Tap to pay →" : ""}
                />
              </div>
            </section>

            {/* Orders */}
            <section className="space-y-3" data-testid="my-orders-list">
              <div className="text-xs font-mono-num tracking-widest text-slate-400 px-1">
                // YOUR ORDERS
              </div>
              {data.orders.length === 0 ? (
                <div className="surface p-10 rounded-sm text-center text-slate-500">
                  <Package size={40} className="mx-auto mb-3 text-slate-700"/>
                  No orders yet.
                </div>
              ) : data.orders.map((o) => (
                <OrderProgressCard key={o.order_number} order={o} onPay={() => setPayTarget(o)} />
              ))}
            </section>

            <ShopContactCard shop={data.shop} />
          </>
        )}
      </main>

      {/* Balance picker — top summary tile click. Lets customer choose which
          unpaid order to settle first, OR pay everything at once. */}
      <BalancePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        orders={data?.orders || []}
        onPick={(o) => { setPickerOpen(false); setPayTarget(o); }}
        onPickCombined={(total) => {
          setPickerOpen(false);
          setCombinedPay({ total, mobile });
        }}
      />

      <PayOnlineDialog
        open={!!payTarget}
        onClose={() => setPayTarget(null)}
        orderNumber={payTarget?.order_number}
        balance={payTarget?.balance}
      />

      <PayOnlineDialog
        open={!!combinedPay}
        onClose={() => setCombinedPay(null)}
        variant="combined"
        mobile={combinedPay?.mobile}
        balance={combinedPay?.total}
      />
    </div>
  );
}

/* --------- Summary tile ---------- */
function SummaryTile({ label, value, tone = "orange", icon, testid, highlight, onClick, hint }) {
  const toneMap = {
    blue: {
      border: "border-cyan-500/50",
      bg: "bg-gradient-to-br from-cyan-500/15 via-cyan-500/5 to-transparent",
      label: "text-cyan-300",
      value: "text-cyan-100",
      icon: "text-cyan-300",
    },
    emerald: {
      border: "border-emerald-500/50",
      bg: "bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent",
      label: "text-emerald-300",
      value: "text-emerald-100",
      icon: "text-emerald-300",
    },
    orange: {
      border: "border-orange-500/60",
      bg: "bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent",
      label: "text-orange-300",
      value: "text-orange-100",
      icon: "text-orange-300",
    },
  };
  const t = toneMap[tone] || toneMap.orange;
  const clickable = typeof onClick === "function";
  const Component = clickable ? "button" : "div";
  return (
    <Component
      onClick={clickable ? onClick : undefined}
      type={clickable ? "button" : undefined}
      className={
        "rounded-sm border-2 p-4 md:p-5 relative overflow-hidden text-left w-full " +
        t.border + " " + t.bg + (highlight ? " ring-1 ring-orange-500/40" : "") +
        (clickable ? " cursor-pointer transition-transform hover:scale-[1.01] hover:border-orange-400 active:scale-[0.99]" : "")
      }
      data-testid={testid}
    >
      <div className={"flex items-center justify-between gap-2 " + t.label}>
        <div className={"flex items-center gap-2 text-[11px] font-mono-num tracking-widest " + t.label}>
          <span className={t.icon}>{icon}</span>
          {label.toUpperCase()}
        </div>
        {clickable && hint && (
          <span className="text-[10px] font-mono-num tracking-widest text-orange-400 bg-orange-500/10 border border-orange-500/40 px-1.5 py-0.5 rounded-sm animate-pulse" data-testid={`${testid}-hint`}>
            {hint}
          </span>
        )}
      </div>
      <div className={"font-heading font-black mt-2 " + t.value + " text-3xl md:text-4xl font-mono-num leading-none"}>
        ₹{Number(value || 0).toLocaleString("en-IN")}
      </div>
    </Component>
  );
}

/* --------- Order card with chevron progress ---------- */
function OrderProgressCard({ order, onPay }) {
  const stepIdx = currentVisualStep(order.status);
  const meta = STATUS_META[order.status] || STATUS_META.new_order;
  const isPaid = (order.balance || 0) <= 0 && (order.total_amount || 0) > 0;
  const canPay = !isPaid && (order.balance || 0) > 0 && order.status !== "delivered";
  const dateStr = order.created_at
    ? new Date(order.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  return (
    <div className="surface p-4 md:p-6 rounded-sm relative" data-testid={`order-card-${order.order_number}`}>
      {/* Top row: order number + status ribbon + FULL DETAIL */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-heading font-black text-white text-2xl md:text-3xl font-mono-num tracking-tight">
              #{order.order_number}
            </span>
            {order.project_type === "gate_railing" && (
              <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/50 text-cyan-300">
                🚪 GATE / RAILING
              </span>
            )}
            <StatusRibbon meta={meta} />
          </div>
          <div className="text-[11px] font-mono-num text-slate-500 mt-1.5 flex items-center gap-1">
            <Clock size={11} weight="bold" /> {dateStr}
          </div>
        </div>
        <Link
          to={`/track/${order.order_number}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 text-xs font-mono-num tracking-widest transition-colors"
          data-testid={`order-detail-${order.order_number}`}
        >
          FULL DETAIL <ArrowRight size={12} weight="bold"/>
        </Link>
      </div>

      {/* Chevron progress bar */}
      <div className="mt-5" data-testid={`order-progress-${order.order_number}`}>
        <ChevronProgress currentIdx={stepIdx} status={order.status} />
      </div>

      {/* Amounts row */}
      <div className="grid grid-cols-3 gap-3 md:gap-6 mt-6">
        <AmountBlock
          label="TOTAL"
          value={order.total_amount}
          icon={<Receipt size={16} weight="bold" className="text-slate-400"/>}
          valueClass="text-slate-100"
        />
        <AmountBlock
          label="PAID"
          value={order.advance_paid}
          icon={
            <span className="w-5 h-5 rounded-full bg-emerald-500 text-slate-900 flex items-center justify-center">
              <CheckCircle size={12} weight="fill"/>
            </span>
          }
          valueClass="text-emerald-300"
        />
        <AmountBlock
          label="BALANCE"
          value={order.balance}
          icon={
            isPaid
              ? <CheckCircle size={16} weight="fill" className="text-emerald-400"/>
              : <Wallet size={16} weight="fill" className="text-orange-400"/>
          }
          valueClass={isPaid ? "text-emerald-300" : "text-orange-300"}
          suffix={isPaid && (order.total_amount || 0) > 0 ? " ✓" : ""}
          onClick={canPay ? onPay : undefined}
          hint={canPay ? "PAY NOW" : ""}
          testid={`balance-block-${order.order_number}`}
        />
      </div>

      {order.total_sqft ? (
        <div className="mt-4 text-[11px] font-mono-num text-slate-500 flex items-center gap-1.5">
          <Ruler size={12} weight="bold"/> {order.total_sqft} sq.ft cutting work
        </div>
      ) : null}
    </div>
  );
}

function AmountBlock({ label, value, icon, valueClass = "text-slate-100", suffix = "", onClick, hint, testid }) {
  const clickable = typeof onClick === "function";
  const Component = clickable ? "button" : "div";
  return (
    <Component
      onClick={clickable ? onClick : undefined}
      type={clickable ? "button" : undefined}
      className={
        "text-left w-full " +
        (clickable
          ? "-m-2 p-2 rounded-sm border border-orange-500/40 hover:border-orange-400 hover:bg-orange-500/5 transition-colors cursor-pointer"
          : "")
      }
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label}</div>
        {clickable && hint && (
          <span className="text-[9px] font-mono-num tracking-widest text-orange-400 bg-orange-500/15 border border-orange-500/40 px-1 py-0.5 rounded-sm">
            {hint} →
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span>{icon}</span>
        <span className={"font-mono-num font-black text-lg md:text-xl " + valueClass}>
          ₹{Number(value || 0).toLocaleString("en-IN")}{suffix}
        </span>
      </div>
    </Component>
  );
}

/* --------- Status ribbon (starburst pill) ---------- */
function StatusRibbon({ meta }) {
  const toneMap = {
    orange:  "bg-orange-500/15 border-orange-500/50 text-orange-300",
    cyan:    "bg-cyan-500/15 border-cyan-500/50 text-cyan-300",
    amber:   "bg-amber-500/15 border-amber-500/50 text-amber-300",
    emerald: "bg-emerald-500/15 border-emerald-500/50 text-emerald-300",
    slate:   "bg-slate-500/15 border-slate-500/50 text-slate-300",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] font-mono-num tracking-widest font-bold " +
        (toneMap[meta.tone] || toneMap.orange)
      }
      data-testid="order-status-ribbon"
    >
      <Star size={10} weight="fill"/> {meta.label}
    </span>
  );
}

/* --------- Chevron progress ---------- */
function ChevronProgress({ currentIdx, status }) {
  const total = VISUAL_STEPS.length;
  const isDelivered = status === "delivered";

  return (
    <div className="flex items-stretch w-full gap-1" data-testid="chevron-progress">
      {VISUAL_STEPS.map((step, i) => {
        const done = i < currentIdx || isDelivered;
        const active = i === currentIdx && !isDelivered;
        // Chevron shape: right-pointing arrow for all segments except last; last has flat right edge.
        const isLast = i === total - 1;
        const isFirst = i === 0;
        const notchPx = 14; // chevron point size
        const clipStyle = {
          clipPath: isLast
            ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${notchPx}px 50%)`
            : isFirst
            ? `polygon(0 0, calc(100% - ${notchPx}px) 0, 100% 50%, calc(100% - ${notchPx}px) 100%, 0 100%)`
            : `polygon(0 0, calc(100% - ${notchPx}px) 0, 100% 50%, calc(100% - ${notchPx}px) 100%, 0 100%, ${notchPx}px 50%)`,
        };

        const classes = active
          ? "bg-gradient-to-r from-orange-500 to-orange-400 text-white shadow-[0_0_20px_rgba(255,107,34,0.55)]"
          : done
          ? "bg-emerald-500/85 text-slate-900"
          : "bg-slate-700/70 text-slate-400";

        return (
          <div
            key={step.key + i}
            style={clipStyle}
            className={
              "flex-1 min-w-0 h-10 md:h-11 flex items-center justify-center transition-all " + classes
            }
            data-testid={`chevron-step-${step.label.toLowerCase()}`}
            title={step.label}
          >
            <span className={
              "text-[10px] md:text-xs font-heading font-black tracking-widest px-2 truncate " +
              (active ? "drop-shadow-sm" : "")
            }>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* --------- Shop contact card ---------- */
function ShopContactCard({ shop }) {
  const rawPhone = (shop?.phone || "").replace(/\D/g, "");
  const shopName = shop?.name || "the workshop";

  if (!rawPhone) {
    return (
      <div className="surface p-6 rounded-sm text-center relative overflow-hidden" data-testid="shop-contact-card">
        <Sparkle className="absolute top-3 right-6 text-slate-700/40 pointer-events-none" size={22} weight="fill" />
        <Sparkle className="absolute bottom-3 left-6 text-slate-700/40 pointer-events-none" size={18} weight="fill" />
        <div className="text-slate-300 text-sm">
          Doubts hain? {shopName} se seedha shop pe aakar baat karein.
        </div>
        <div className="text-[11px] font-mono-num text-slate-500 mt-2">
          // Shop admin: add your phone in <b>Settings → Company Info → Phone</b> to show contact buttons.
        </div>
      </div>
    );
  }

  const whatsAppNum = rawPhone.length === 10 ? `91${rawPhone}` : rawPhone;
  return (
    <div className="surface p-6 md:p-8 rounded-sm text-center relative overflow-hidden" data-testid="shop-contact-card">
      <Sparkle className="absolute top-3 right-6 text-slate-700/40 pointer-events-none" size={24} weight="fill" />
      <Sparkle className="absolute bottom-3 left-6 text-slate-700/40 pointer-events-none" size={20} weight="fill" />
      <div className="text-slate-100 text-base md:text-lg font-heading">
        Doubts hain? Workshop se baat karein.
      </div>
      <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
        <a
          href={`tel:+${whatsAppNum}`}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-heading tracking-widest shadow-[0_6px_20px_rgba(37,99,235,0.35)] transition-transform hover:-translate-y-0.5"
          data-testid="my-orders-call-shop"
        >
          <Phone size={16} weight="fill"/> CALL
        </a>
        <a
          href={`https://wa.me/${whatsAppNum}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-heading tracking-widest shadow-[0_6px_20px_rgba(16,185,129,0.35)] transition-transform hover:-translate-y-0.5"
          data-testid="my-orders-whatsapp-shop"
        >
          <WhatsappLogo size={16} weight="fill"/> WHATSAPP
        </a>
      </div>
    </div>
  );
}

/* --------- BalancePicker — when customer taps top BALANCE tile ---------- */
function BalancePicker({ open, onClose, orders, onPick, onPickCombined }) {
  // Only show orders that still have a balance due. Sort by oldest first
  // — that's usually the one customers want to settle first.
  const dueOrders = (orders || []).filter(
    (o) => (o.balance || 0) > 0 && o.status !== "delivered"
  );
  const totalDue = dueOrders.reduce((s, o) => s + (Number(o.balance) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm p-0 overflow-hidden"
        data-testid="balance-picker-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// PAY DUE BALANCE</div>
          <DialogTitle className="font-heading text-xl mt-1">
            Which order to settle?
          </DialogTitle>
          <p className="text-xs text-slate-400 mt-1">
            Ek order alag pay karein <b>ya neeche</b> "PAY TOTAL" dabaake sab ek baar mein settle karein.
          </p>
        </DialogHeader>
        <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
          {dueOrders.length === 0 ? (
            <div className="text-center text-slate-500 py-6 text-sm">
              Sabhi orders paid ho chuke hain 🎉
            </div>
          ) : dueOrders.map((o) => (
            <button
              key={o.order_number}
              onClick={() => onPick(o)}
              className="w-full text-left px-4 py-3 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 transition-colors flex items-center justify-between gap-3"
              data-testid={`balance-picker-item-${o.order_number}`}
            >
              <div>
                <div className="font-heading text-white font-black">
                  #{o.order_number}
                </div>
                <div className="text-[10px] font-mono-num text-slate-500 mt-0.5">
                  {o.status?.replace(/_/g, " ").toUpperCase()}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-num text-lg font-black text-orange-300">
                  ₹{Number(o.balance || 0).toLocaleString("en-IN")}
                </div>
                <div className="text-[10px] font-mono-num text-orange-400 tracking-widest">
                  PAY NOW →
                </div>
              </div>
            </button>
          ))}
        </div>
        {dueOrders.length > 0 && (
          <div className="border-t border-slate-800 bg-slate-800/40 px-5 py-4 space-y-3" data-testid="balance-picker-total-section">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL DUE ACROSS {dueOrders.length} ORDER{dueOrders.length === 1 ? "" : "S"}</div>
                <div className="font-mono-num text-3xl font-black text-orange-300 leading-none mt-1" data-testid="balance-picker-total">
                  ₹{totalDue.toLocaleString("en-IN")}
                </div>
              </div>
              <CurrencyInr size={38} weight="bold" className="text-orange-500/40" />
            </div>
            <Button
              onClick={() => onPickCombined(totalDue)}
              className="w-full h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest shadow-lg shadow-emerald-500/20"
              data-testid="pay-total-btn"
            >
              <CurrencyInr size={20} weight="bold" className="mr-2" />
              PAY TOTAL ₹{totalDue.toLocaleString("en-IN")}
            </Button>
            <div className="text-[10px] font-mono-num text-slate-500 text-center leading-relaxed">
              Ek hi UPI transaction mein pura balance settle ho jaayega — workshop bank statement se orders adjust kar lega.
            </div>
          </div>
        )}
        <div className="px-5 py-3 border-t border-slate-800 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="balance-picker-close"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

